import Ajv from 'ajv';
import { map } from 'lodash/fp';

import { validateConditionalRules } from 'dashboard/lib/workflows/validation/validateConditionalStep';
import { WorkflowValidationContext } from 'dashboard/lib/workflows/validation/validationContext';
import { TriggerObjectTypes } from 'dashboard/models/trigger';
import { WorkflowConfigError, WorkflowErrorType } from 'dashboard/models/workflow';
import { INPUT_STEP_TOKEN, WorkflowConditional } from 'dashboard/models/workflow-version';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

export const validateInputStep = (
  validationContext: WorkflowValidationContext,
): WorkflowConfigError[] => {
  const { inputStep, getContextForStep } = validationContext;
  const { trigger, inputs } = inputStep;
  const { jsonSchema } = getContextForStep(inputStep.token);

  if (inputs.length) {
    const names = map('name', inputs);
    const inputErrors: WorkflowConfigError[] = inputs.flatMap(({ name, token }) => {
      const isNameDuplicate = names.filter((n) => n === name).length > 1;
      return isNameDuplicate
        ? [
            {
              token: INPUT_STEP_TOKEN,
              field: `inputs.${token}`,
              message: 'Input name must be unique',
              type: WorkflowErrorType.Save,
            },
          ]
        : [];
    });

    if (inputErrors.length) {
      return inputErrors;
    }
  }

  if (trigger) {
    switch (trigger.type) {
      case ApiModel.TriggerComputedField: {
        if (trigger.attributes['config'].length < 1) {
          return [
            {
              token: INPUT_STEP_TOKEN,
              field: 'config',
              message: 'At least one event required',
              type: WorkflowErrorType.Publish,
            },
          ];
        }

        if (!trigger.attributes['field-key']) {
          return [
            {
              token: INPUT_STEP_TOKEN,
              field: 'field-key',
              message: 'Field key required',
              type: WorkflowErrorType.Publish,
            },
          ];
        }
        return [];
      }
      case ApiModel.TriggerSchedule: {
        return !trigger.attributes['trigger-object-type'] ||
          !TriggerObjectTypes.find((one) => one === trigger.attributes['trigger-object-type'])
          ? [
              {
                token: INPUT_STEP_TOKEN,
                field: 'trigger-object-type',
                message: 'Trigger object type should be selected',
                type: WorkflowErrorType.Publish,
              },
            ]
          : [];
      }

      case ApiModel.TriggerRecurring: {
        if (!trigger.attributes['start-at'] && trigger.attributes['rrule'] !== 'FREQ=TEST;') {
          return [
            {
              token: INPUT_STEP_TOKEN,
              field: 'start-at',
              message: 'Start date required',
              type: WorkflowErrorType.Publish,
            },
          ];
        }

        if (!trigger.attributes['rrule']) {
          return [
            {
              token: INPUT_STEP_TOKEN,
              field: 'rrule',
              message: 'Schedule required',
              type: WorkflowErrorType.Publish,
            },
          ];
        }

        if (
          trigger.attributes['trigger-object-type'] != null &&
          (!trigger.attributes['trigger-object-type'] ||
            !TriggerObjectTypes.find((one) => one === trigger.attributes['trigger-object-type']))
        ) {
          return [
            {
              token: INPUT_STEP_TOKEN,
              field: 'trigger-object-type',
              message: 'Trigger object type should be selected',
              type: WorkflowErrorType.Publish,
            },
          ];
        }

        if (
          trigger.attributes['trigger-object-type'] != null &&
          (!trigger.attributes['filter'] || trigger.attributes['filter'].length === 0)
        ) {
          return [
            {
              token: INPUT_STEP_TOKEN,
              field: 'filter',
              message: 'At least one filter criteria required',
              type: WorkflowErrorType.Publish,
            },
          ];
        }

        const invalidFilters = trigger.attributes['filter'].filter((one) => {
          if (!one['name'] || !one['operator'] || !one['value']) {
            return true;
          }
          return false;
        });

        if (invalidFilters.length > 0) {
          return [
            {
              token: INPUT_STEP_TOKEN,
              field: 'filter',
              message: 'Invalid filter configurations',
              type: WorkflowErrorType.Publish,
            },
          ];
        }
        return [];
      }

      case ApiModel.TriggerApi: {
        const ajv = new Ajv();
        const jsonFieldSchema = trigger.attributes['param-schema'][0].config['json-schema'];
        if (!ajv.validateSchema(JSON.parse(jsonFieldSchema))) {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          return ajv.errors.map((error) => ({
            token: INPUT_STEP_TOKEN,
            field: 'param-schema',
            message: `${error.instancePath}: ${error.message}`,
            type: WorkflowErrorType.Publish,
          }));
        }
        return [];
      }
      case ApiModel.TriggerEvent: {
        if (trigger.attributes.rules === true) {
          return [];
        }
        return validateConditionalRules(
          { rules: trigger.attributes.rules } as WorkflowConditional,
          validationContext,
          INPUT_STEP_TOKEN,
          jsonSchema,
        );
      }
      case ApiModel.TriggerCase:
        return [];
      case ApiModel.TriggerAccount:
        //TODO(zixiao): validate json schema for account trigger
        return [];
      case ApiModel.TriggerDirect:
        return [];
      default:
        assertNever(trigger);
    }
  }

  return [];
};
